import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import type { RootState } from "../../../redux/store";
import { setUserContext } from "../../../redux/slices/UserSlice";
import { userDataApi } from "../../../Apis/login";
import { identifyUser } from "../../../analytics";
import { isPermissionGroupContextError } from "../../../utils/teamLeaderAccess";
import PermissionGroupErrorScreen from "../PermissionGroupErrorScreen/PermissionGroupErrorScreen";
import { CenteredPageLoader } from "../CenteredPageLoader/CenteredPageLoader";

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const isAuthRehydrated = (state: RootState): boolean => {
  const persist = state._persist;
  return persist == null || persist.rehydrated;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const userProfile = useAppSelector((state) => state.userProfile);
  const authRehydrated = useAppSelector((state) => isAuthRehydrated(state));
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [contextLoadFailed, setContextLoadFailed] = useState(false);
  const [permissionGroupError, setPermissionGroupError] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);

  // Fetch user context when token exists but userProfile is null (e.g. page refresh)
  useEffect(() => {
    if (!accessToken || userProfile) {
      setContextLoading(false);
      return;
    }
    let cancelled = false;
    setContextLoading(true);
    setContextLoadFailed(false);
    setPermissionGroupError(false);
    userDataApi()
      .then((data) => {
        if (!cancelled) {
          dispatch(setUserContext(data));
          // Re-arm analytics on session restore: identifyUser() re-identifies
          // and calls ready(), un-pausing the SDK's disabled-start queue. A
          // page refresh skips the login path, so without this every event
          // after a refresh queued in memory forever (P0 found in live test).
          // Deliberately NO login_succeeded here — a restore is not a login.
          identifyUser(data);
          setContextLoadFailed(false);
          setPermissionGroupError(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load user context:", err);
        if (isPermissionGroupContextError(err)) {
          setPermissionGroupError(true);
        } else {
          setContextLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, userProfile, dispatch]);

  if (!authRehydrated) {
    return (
      <div
        id="protected-route-rehydration-loader"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CenteredPageLoader
          message="Loading session..."
          id="protected-route-rehydration-loader-spinner"
        />
      </div>
    );
  }

  // If no accessToken, redirect to login page
  // Preserve the attempted location in state so we can redirect back after login
  if (!accessToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (permissionGroupError) {
    return <PermissionGroupErrorScreen />;
  }

  if (contextLoading || (!userProfile && !contextLoadFailed)) {
    return (
      <div
        id="protected-route-context-loader"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CenteredPageLoader
          message="Loading account..."
          id="protected-route-context-loader-spinner"
        />
      </div>
    );
  }

  // If accessToken exists, render the protected route
  return children;
};

export default ProtectedRoute;
